import {useEffect, useState} from "react";
import {useLocation} from "react-router";

import {parseSearch} from "@pg-mono/request";

import {useParseFriendlyUrl} from "../../offer/hooks/use_parse_friendly_url";
import {ILatestQuery} from "../../utils/latest_query";
import {IRecentFilter} from "../utils/recent_filters_storage";
import {getRecentFilters} from "../utils/recent_filters_storage";

/**
 * Gets latestQuery from URL (both query params and friendly URL)
 */
export const useLatestQueryFromUrl = (): ILatestQuery => {
    const location = useLocation();
    const {query} = useParseFriendlyUrl();
    const parsedSearch = parseSearch(location.search);

    return (query ?? parsedSearch) as ILatestQuery;
};

/**
 * Checks if main filters (price, area, rooms, instalment, type, construction_end_date) are present in URL
 */
export const hasMainFiltersInUrl = (latestQuery: ILatestQuery): boolean => {
    const hasArea = !!(latestQuery.area_0 || latestQuery.area_1);
    const hasRooms = !!(latestQuery.rooms_0 || latestQuery.rooms_1);
    const hasPrice = !!(latestQuery.price_0 || latestQuery.price_1);
    const hasInstalment = !!(latestQuery.instalment && latestQuery.instalment !== "");
    const hasType = !!(latestQuery.type && latestQuery.type !== "");
    const hasConstructionEndDate = !!(latestQuery.construction_end_date && latestQuery.construction_end_date !== "");

    return hasArea || hasRooms || hasPrice || hasInstalment || hasType || hasConstructionEndDate;
};

/**
 * Checks if recentFilter has main filters (price, area, rooms, instalment, type, construction_end_date)
 */
export const hasMainFiltersInRecentFilter = (recentFilter: IRecentFilter | null): boolean => {
    if (!recentFilter?.filters) {
        return false;
    }

    const hasArea = !!(recentFilter.filters.area?.lower || recentFilter.filters.area?.upper);
    const hasRooms = !!(recentFilter.filters.rooms?.lower || recentFilter.filters.rooms?.upper);
    const hasPrice = !!(recentFilter.filters.price?.lower || recentFilter.filters.price?.upper);
    const instalment = recentFilter.filters.instalment;
    const hasInstalment = !!(instalment !== undefined && instalment !== "" && typeof instalment === "number");
    const hasType = Array.isArray(recentFilter.filters.type) && recentFilter.filters.type.length > 0;
    const hasConstructionEndDate = Array.isArray(recentFilter.filters.construction_end_date) && recentFilter.filters.construction_end_date.length > 0;

    return hasArea || hasRooms || hasPrice || hasInstalment || hasType || hasConstructionEndDate;
};

/**
 * Hook to manage recent filters state and validation (up to 5 recent searches).
 */
export const useRecentFilters = () => {
    const [recentFilters, setRecentFilters] = useState<IRecentFilter[]>([]);
    const [isMounted, setIsMounted] = useState(false);
    const latestQuery = useLatestQueryFromUrl();

    useEffect(() => {
        setIsMounted(true);
        setRecentFilters(getRecentFilters());
    }, []);

    const shouldShow = isMounted && recentFilters.length > 0 && !hasMainFiltersInUrl(latestQuery) && recentFilters.some((f) => hasMainFiltersInRecentFilter(f));

    return {
        recentFilters,
        latestQuery,
        isMounted,
        shouldShow
    };
};
